import React from "react";
import { Row, Col, Button, Tag, Space, Popover, Badge } from "antd";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	QuestionCircleOutlined,
	RightCircleOutlined,
	ClockCircleOutlined,
	CommentOutlined,
	SendOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import EllipsisDropdownMenu from "components/EllipsisDropdownMenu";
import { useSelector } from "react-redux";
import { selectCommentByWorkflowId } from "reducers/workflowHistorySlice";
import { useSecurity } from "../cme/Components/security";
import { selectGlobal } from "reducers/global";
import { getTimezone } from "utils/common";
import dayjs from "dayjs";

export const ApprovalButton = ({ onClick }) => {
	return (
		<Button type="primary" size="large" onClick={onClick}>
			<CheckCircleOutlined />
			Approve
		</Button>
	);
};

export const DeclineButton = ({ onClick }) => {
	return (
		<Button type="danger" size="large" onClick={onClick}>
			<CloseCircleOutlined />
			Decline
		</Button>
	);
};

export const QueryButton = ({ onClick }) => {
	return (
		<Button type="default" size="large" onClick={onClick}>
			<ReloadOutlined />
			Query
		</Button>
	);
};

export const DelegateButton = ({ onClick }) => {
	return (
		<Button type="default" size="large" onClick={onClick}>
			<SendOutlined />
			Delegate
		</Button>
	);
};

export const RespondButton = ({ onClick }) => {
	return (
		<Button type="primary" size="large" onClick={onClick}>
			<CommentOutlined />
			Respond
		</Button>
	);
};

export const StatusTag = ({ status }) => {
	switch (status) {
		case "Approved":
			return (
				<Tag icon={<CheckCircleOutlined />} color="success">
					Approved
				</Tag>
			);
		case "Responded":
			return (
				<Tag icon={<CheckCircleOutlined />} color="success">
					Responded
				</Tag>
			);
		case "Declined":
			return (
				<Tag icon={<CloseCircleOutlined />} color="error">
					Declined
				</Tag>
			);
		case "Queried":
			return (
				<Tag icon={<QuestionCircleOutlined />} color="blue">
					Queried
				</Tag>
			);
		case "Delegated":
			return (
				<Tag icon={<RightCircleOutlined />} color="blue">
					Delegated
				</Tag>
			);
		case "Waiting":
			return (
				<Tag icon={<ClockCircleOutlined />} color="default">
					Waiting
				</Tag>
			);
		/* we need to return a space instead of null/empty as this causes issues on Edge 84, where a row-gap on empty causes this to increase the container's height to infinity */
		default:
			return <> </>;
	}
};

export const ActionButtons = ({
	workflow,
	roleId,
	organisationId,
	onEdit,
	onDelete,
}) => {
	//available actions takes state and current user's permissions
	//and returns available buttons

	/*
    SKIP: authorisation if
      -this workflow is active (next in line)
      -user is on this workflow
      -user has higher position in hierarchy than this workflow
        (prevents self skip)
  */
	const { isCmeAdmin } = useSecurity();
	const isCme = useSelector(selectGlobal).appType === "cme";
	let listMenu = [
		{
			label: "Change this approver",
			onClick: () => onEdit({ workflow, roleId, organisationId }),
		},
	];

	if (isCmeAdmin || !isCme)
		listMenu.push({
			label: "Delete this approver",
			onClick: () => onDelete({ workflow }),
		});

	return <EllipsisDropdownMenu listMenu={listMenu} />;
};

export const approvalRowStyle = {
	padding: 10,
	borderBottom: "1px solid #ddd",
};

export const Workflow = ({
	id,
	data,
	onEdit,
	onDelete,
	sequence,
	organisationHierarchyId,
	display,
}) => {
	const {
		user,
		role,
		status,
		isActive: wfIsActive,
		isComplete,
		delegateUsers,
		completingUser,
		description,
		modifyOn,
	} = data;
	const comments = useSelector((state) =>
		selectCommentByWorkflowId(state, { id })
	);
	const isActive = wfIsActive;
	const activeStyle = {
		borderTop: "1px solid lightseagreen",
		borderBottom: "1px solid lightseagreen",
		borderLeft: "1px solid lightseagreen",
		borderRight: "1px solid lightseagreen",
		fontWeight: 600,
		background: "rgba(32, 178, 170,0.1)",
	};

	return (
		<>
			<Row
				style={{
					...approvalRowStyle,
					fontWeight: 300,
					...(isActive ? activeStyle : {}),
				}}
				align="middle"
			>
				<Col span={1}>{sequence}</Col>
				<Col
					span={15}
					md={8}
					style={{ wordBreak: "break-all", paddingRight: 5 }}
				>
					<WorkflowPerson
						isComplete={isComplete}
						user={user}
						delegateUsers={delegateUsers}
						completingUser={completingUser}
					/>
					{display === "role" || !user ? role?.title : user?.jobTitle}
				</Col>
				<Col span={7} md={6}>
					<StatusTag status={isActive ? "Waiting" : status} />
					{isComplete && status !== "Skipped" && (
						<div style={{ fontSize: "small", paddingTop: 5 }}>
							{getTimezone() === "UTC"
								? dayjs
										.utc(modifyOn)
										.local()
										.format("DD/MM/YYYY hh:mma")
								: dayjs(modifyOn).format("DD/MM/YYYY hh:mma")}
						</div>
					)}
				</Col>
				{comments && (
					<Col span={23} md={0}>
						<Space direction="horizontal">
							<CommentOutlined style={{ color: "#AAA" }} />
							{comments}
						</Space>
					</Col>
				)}
				<Col span={0} md={8}>
					{comments && (
						<Space direction="horizontal">
							<CommentOutlined style={{ color: "#AAA" }} />
							{comments}
						</Space>
					)}
				</Col>
				<Col span={1}>
					{!isComplete && (
						<ActionButtons
							id={id}
							roleId={role?.id}
							organisationId={organisationHierarchyId}
							onEdit={onEdit}
							onDelete={onDelete}
							workflow={data}
						/>
					)}
				</Col>
				{description && (
					<Col
						span={23}
						offset={1}
						style={{ fontSize: "smaller", marginTop: 10 }}
					>
						{description}
					</Col>
				)}
			</Row>
		</>
	);
};

export const WorkflowPerson = ({
	user,
	delegateUsers,
	completingUser,
	isComplete,
}) => {
	//first user with replacement type of delegate is the replacing user
	const replacingUser =
		delegateUsers && delegateUsers.length > 0
			? delegateUsers.filter((du) => du.type === "REPLACEMENT")[0]?.user
			: null;

	//all other delegate users, including other replacement users
	const otherDelegateUsers =
		delegateUsers && delegateUsers.length > 0
			? delegateUsers.filter((du) => du.userId !== replacingUser?.id)
			: null;

	//the name of the approver to display - replacing user, or the original user
	const workflowUser = !isComplete
		? replacingUser ?? user //for active workflows, we prioritise showing replacing user
		: completingUser ?? user; //for completed workflows, we prioritise showing completing user
	const fullName = `${workflowUser?.firstName} ${workflowUser?.lastName}`;
	const replacedFullName = `${user?.firstName} ${user?.lastName}`;

	const content = (
		<div style={{ width: 400 }}>
			<Row style={{ padding: "5px 5px" }}>
				<Col span={8} style={{ fontWeight: 600 }}>
					Main Approver
				</Col>
				<Col span={16}>{workflowUser?.fullName}</Col>
			</Row>
			<Row style={{ padding: "5px 5px" }}>
				<Col span={8} style={{ fontWeight: 600 }}>
					Email
				</Col>
				<Col span={16}>
					<a href={`mailto:${workflowUser?.email}`}>
						{workflowUser?.email}
					</a>
				</Col>
			</Row>
			<Row style={{ padding: "5px 5px" }}>
				<Col span={8} style={{ fontWeight: 600 }}>
					Job Title
				</Col>
				<Col span={16}>{workflowUser?.jobTitle}</Col>
			</Row>
			{delegateUsers && delegateUsers.length > 0 && !isComplete ? (
				<Row style={{ padding: "5px 5px" }}>
					<Col span={8} style={{ fontWeight: 600 }}>
						Delegate Approver(s)
					</Col>
					<Col span={16}>
						{delegateUsers.map((u) => (
							<React.Fragment key={u?.id}>
								<span>
									{u?.user?.fullName} ({u?.user?.jobTitle})
								</span>
								<br />
							</React.Fragment>
						))}
					</Col>
				</Row>
			) : null}
		</div>
	);

	const ApproverDetailPopover = ({ children }) => {
		return (
			<Popover content={content} title="Approver Details" trigger="hover">
				<div tabIndex={0}>{children}</div>
			</Popover>
		);
	};

	//if there is more 1 or more delegate users that can approve,
	//we show a badge with a count of those users
	const UserDisplayWithBadge = () => {
		return otherDelegateUsers &&
			otherDelegateUsers.length > 0 &&
			!isComplete ? (
			<div>
				<Badge
					count={otherDelegateUsers.length}
					size="small"
					offset={[7, 0]}
				>
					<div className="link default-font-size">{fullName}</div>
				</Badge>
			</div>
		) : (
			<div className="link">{fullName}</div>
		);
	};

	const ReplacingUserText = () => {
		return (
			<div style={{ fontWeight: 100, fontSize: "small" }}>
				replacing {replacedFullName}
			</div>
		);
	};

	const OnBehalfUserText = () => {
		return (
			<div style={{ fontWeight: 100, fontSize: "small" }}>
				on behalf of {user.fullName}
			</div>
		);
	};

	return !user ? (
		<div style={{ textDecoration: "italic", color: "#bbb" }}>
			No approver
		</div>
	) : replacingUser && !isComplete ? (
		<ApproverDetailPopover>
			<UserDisplayWithBadge />
			<ReplacingUserText />
		</ApproverDetailPopover>
	) : isComplete && user?.id !== completingUser?.id && completingUser ? (
		<ApproverDetailPopover>
			<UserDisplayWithBadge />
			<OnBehalfUserText />
		</ApproverDetailPopover>
	) : (
		<ApproverDetailPopover>
			<UserDisplayWithBadge />
		</ApproverDetailPopover>
	);
};

export const WorkflowTypeContainer = ({
	type,
	workflows,
	organisationHierarchyId,
	onEdit,
	onDelete,
	options,
}) => {
	let sequence = 1; //global value for display sequence
	return (
		<>
			{type ? (
				<h3 style={{ marginTop: 15, fontWeight: 300 }}>{type}</h3>
			) : null}
			<Row
				style={{
					...approvalRowStyle,
					fontWeight: 600,
				}}
				align="middle"
			>
				<Col span={1}></Col>
				<Col span={15} md={8}>
					Approver
				</Col>
				<Col span={7} md={6}>
					Status
				</Col>
				<Col span={0} md={8}>
					Comments
				</Col>
				<Col span={1}></Col>
			</Row>
			{workflows?.map((item, index) => {
				const { id } = item;
				return (
					<Workflow
						key={index}
						data={item}
						organisationHierarchyId={organisationHierarchyId}
						sequence={sequence++}
						id={id || sequence}
						onEdit={onEdit}
						onDelete={onDelete}
						display={options?.display}
					/>
				);
			})}
		</>
	);
};
